/**
 * Shared offer list/detail presentation helpers for discovery browse
 * (list rows + DiscoveryOfferDetailSheet).
 *
 * Subtitle order mirrors shops directory: category · shop · tenant · distance.
 * Backend `subtitle` is opaque (`shop · tenant`) — not re-joined here.
 */

import { resolveCatalogDisplayImageUrl } from 'pi-kiosk-shared';
import type { FormatDistanceMetersLabels } from '../../shared/ui/directoryListFilter.js';
import { formatDistanceMeters } from '../../shared/ui/directoryDistance.js';
import type { PublicDiscoveryOfferItemDTO } from '../discovery/publicDiscoveryTypes.js';

/** List/detail/cart peer — thumbnail preferred, tenant-scoped legacy rewrite. */
export function resolveDiscoveryOfferDisplayImageUrl(
  offer: PublicDiscoveryOfferItemDTO,
): string | null {
  return resolveCatalogDisplayImageUrl(offer.thumbnailUrl, offer.imageUrl, {
    tenantCode: offer.tenantContext.tenantCode,
  });
}

function pushUniquePart(parts: string[], value: string): void {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return;
  }
  const norm = trimmed.toLocaleLowerCase();
  if (parts.some((part) => part.toLocaleLowerCase() === norm)) {
    return;
  }
  if (parts.some((part) => part.toLocaleLowerCase().includes(norm) || norm.includes(part.toLocaleLowerCase()))) {
    return;
  }
  parts.push(trimmed);
}

/** Subtitle segments for a discovery offer row (shops-directory peer). */
export function buildDiscoveryOfferSubtitleParts(
  offer: PublicDiscoveryOfferItemDTO,
  distanceFormatter: FormatDistanceMetersLabels,
  options?: { readonly grouped?: boolean },
): string[] {
  const parts: string[] = [];
  pushUniquePart(parts, offer.platformCategory?.name ?? '');
  if (options?.grouped !== true) {
    const shop = offer.salesPointContext.name.trim();
    pushUniquePart(parts, shop);
    const tenant = offer.tenantContext.tenantName.trim();
    if (
      tenant.length > 0 &&
      !shop.toLocaleLowerCase().includes(tenant.toLocaleLowerCase())
    ) {
      pushUniquePart(parts, tenant);
    }
    const distanceLabel = formatOfferDistance(offer, distanceFormatter);
    if (distanceLabel !== null) {
      pushUniquePart(parts, distanceLabel);
    }
  }
  return parts;
}

/** Single-line place/subtitle for detail sheet body copy. */
export function buildOfferPlaceLine(offer: PublicDiscoveryOfferItemDTO): string {
  const parts: string[] = [];
  pushUniquePart(parts, offer.platformCategory?.name ?? '');
  const shop = offer.salesPointContext.name.trim();
  pushUniquePart(parts, shop);
  const tenant = offer.tenantContext.tenantName.trim();
  if (
    tenant.length > 0 &&
    !shop.toLocaleLowerCase().includes(tenant.toLocaleLowerCase())
  ) {
    pushUniquePart(parts, tenant);
  }
  return parts.join(' · ');
}

export function formatOfferDistance(
  offer: PublicDiscoveryOfferItemDTO,
  distanceFormatter: FormatDistanceMetersLabels,
): string | null {
  if (typeof offer.distanceMeters !== 'number') {
    return null;
  }
  return formatDistanceMeters(offer.distanceMeters, distanceFormatter);
}
